import { isPlatformBrowser } from '@angular/common';
import { Injectable, inject, PLATFORM_ID } from '@angular/core';

/**
 * Mobile bottom sheets (<= 844px): drag down to close.
 *
 * One document-level touch listener for every sheet of the app (p-dialog, select overlays,
 * datepicker, pill filters and our own `.bt-sheet` panels), so no component has to wire it.
 * The panel follows the finger; released far enough (or flicked) it closes the same way a tap
 * outside or the hidden X would, otherwise it springs back. It only starts when pulling DOWN
 * with the content scrolled to the top, so scrolling a long sheet still works.
 */
@Injectable({ providedIn: 'root' })
export class SheetDragService {
	private readonly platformId = inject(PLATFORM_ID);

	private static readonly SHEETS = [
		'.p-dialog-mask .p-dialog[role]',
		'.p-overlay-modal .p-overlay-content',
		'.p-datepicker-panel:not(.p-datepicker-panel-inline)',
		'.pill-filter-sheet',
		'.bt-sheet',
	].join(',');

	private static readonly CLOSE_DISTANCE = 110; // px
	private static readonly CLOSE_VELOCITY = 0.6; // px/ms, a quick flick also closes

	private sheet: HTMLElement | null = null;
	private startY = 0;
	private startTime = 0;
	private dy = 0;
	private dragging = false;
	private started = false;

	init(): void {
		if (!isPlatformBrowser(this.platformId)) return;
		document.addEventListener('touchstart', (e) => this.onStart(e), { passive: true, capture: true });
		document.addEventListener('touchmove', (e) => this.onMove(e), { passive: false, capture: true });
		document.addEventListener('touchend', () => this.onEnd(), { capture: true });
		document.addEventListener('touchcancel', () => this.reset(true), { capture: true });
		document.addEventListener('click', (e) => this.onMaskClick(e));
	}

	/**
	 * p-dialog doesn't close on a tap outside (dismissableMask is off everywhere): on a phone it
	 * does, like any sheet. Only dialogs that have an X (closable) — the ones that must stay open
	 * (confirmations, payment in progress) have none and ignore it.
	 */
	private onMaskClick(e: MouseEvent): void {
		if (!this.isMobile) return;
		const mask = e.target as HTMLElement;
		if (!mask.classList?.contains('p-dialog-mask')) return; // the tap landed inside the dialog
		const dialogs = mask.querySelectorAll<HTMLElement>('.p-dialog[role]');
		const dialog = dialogs[dialogs.length - 1];
		dialog?.querySelector<HTMLElement>('.p-dialog-close-button, .p-dialog-header-close')?.click();
	}

	private get isMobile(): boolean {
		return window.innerWidth <= 844;
	}

	private onStart(e: TouchEvent): void {
		if (!this.isMobile || e.touches.length !== 1) return;
		const target = e.target as HTMLElement;
		const sheet = target.closest<HTMLElement>(SheetDragService.SHEETS);
		// Reordering lists (cdkDrag) and form fields keep their own gestures
		if (!sheet || target.closest('.cdk-drag, input, textarea, [contenteditable="true"]')) return;
		if (!this.scrolledToTop(target, sheet)) return;

		this.sheet = sheet;
		this.startY = e.touches[0].clientY;
		this.startTime = performance.now();
		this.dy = 0;
		this.started = false;
		this.dragging = false;
	}

	private onMove(e: TouchEvent): void {
		if (!this.sheet) return;
		const dy = e.touches[0].clientY - this.startY;

		if (!this.started) {
			if (Math.abs(dy) < 6) return;
			this.started = true;
			// Pulling up (or sideways scroll) is not ours
			if (dy < 0) {
				this.sheet = null;
				return;
			}
			this.dragging = true;
			this.sheet.style.transition = 'none';
		}
		if (!this.dragging) return;

		e.preventDefault(); // the page/content must not scroll while the sheet follows the finger
		this.dy = Math.max(0, dy);
		this.sheet.style.translate = `0 ${this.dy}px`;
		this.sheet.style.setProperty('--bt-drag-y', `${this.dy}px`);
	}

	private onEnd(): void {
		if (!this.sheet || !this.dragging) {
			this.sheet = null;
			return;
		}
		const velocity = this.dy / Math.max(1, performance.now() - this.startTime);
		const close = this.dy > SheetDragService.CLOSE_DISTANCE || (this.dy > 40 && velocity > SheetDragService.CLOSE_VELOCITY);
		const sheet = this.sheet;

		if (close) {
			// The leave animation starts from --bt-drag-y (see _sheets.scss), so it goes on down
			sheet.style.translate = '';
			sheet.style.transition = '';
			this.close(sheet);
			// A sheet that can't be closed this way comes back after its (non-)leave
			setTimeout(() => {
				if (sheet.isConnected) this.springBack(sheet);
			}, 400);
		} else {
			this.springBack(sheet);
		}
		this.sheet = null;
		this.dragging = false;
	}

	private reset(springBack: boolean): void {
		if (springBack && this.sheet && this.dragging) this.springBack(this.sheet);
		this.sheet = null;
		this.dragging = false;
	}

	private springBack(sheet: HTMLElement): void {
		sheet.style.transition = 'translate 0.25s cubic-bezier(0.32, 0.72, 0, 1)';
		sheet.style.translate = '0 0';
		sheet.style.setProperty('--bt-drag-y', '0px');
		setTimeout(() => {
			sheet.style.transition = '';
			sheet.style.translate = '';
		}, 260);
	}

	/** True when nothing between the finger and the sheet is scrolled down */
	private scrolledToTop(target: HTMLElement, sheet: HTMLElement): boolean {
		for (let el: HTMLElement | null = target; el && el !== sheet.parentElement; el = el.parentElement) {
			if (el.scrollTop > 0) return false;
		}
		return true;
	}

	/** Closes it the way each kind of sheet already closes on its own */
	private close(sheet: HTMLElement): void {
		// p-dialog: its close button (hidden on mobile, still there). Without one the dialog must
		// stay open (confirmation, payment in progress): it springs back
		if (sheet.matches('.p-dialog')) {
			sheet.querySelector<HTMLElement>('.p-dialog-close-button, .p-dialog-header-close')?.click();
			return;
		}

		// CDK overlays (pill filters): their backdrop
		const pane = sheet.closest('.cdk-overlay-pane');
		const backdrop = pane?.previousElementSibling;
		if (backdrop?.classList.contains('cdk-overlay-backdrop')) {
			(backdrop as HTMLElement).click();
			return;
		}

		// Our sheets: the veil around them closes on click
		const veil = sheet.parentElement?.closest<HTMLElement>('[class*="overlay"], [class*="backdrop"]');
		if (sheet.matches('.bt-sheet') && veil) {
			veil.click();
			return;
		}

		// Select / datepicker overlays: a tap outside, then Escape
		const outside = sheet.closest<HTMLElement>('.p-overlay-modal') ?? document.body;
		for (const type of ['mousedown', 'mouseup', 'click']) {
			outside.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true, view: window }));
		}
		document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', bubbles: true }));
	}
}
